<script setup>
/** 回合时间线列表：一段时间内四个关键指标的 p50 / p90，以及每个回合的耗时分布；点一行进时间轴。 */
import { computed, onMounted, ref } from 'vue'
import { Search } from '@lucide/vue'
import { Badge, Button, EmptyState, Input, PageHeader, Pagination, Section, Select, Spinner, Table, Toolbar } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { listTurnTimelines } from '../../api/llm/index.js'
import { formatDateTime } from './llm-format.js'
import { MOMENT_METRICS, formatMoment, formatSeconds } from './turn-timeline/timeline-layout.js'
import TurnTimelineMetrics from './turn-timeline/TurnTimelineMetrics.vue'

const WINDOWS = {
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000,
}
const PAGE_SIZES = [20, 50, 100]
const METRIC_KEYS = ['longest_gap', 'idle_total', 'first_feedback', 'npc_first_text', 'npc_first_sound', 'input_unlocked']

const { t } = useI18n()
const router = useRouter()
const windowKey = ref('day')
const turnIdInput = ref('')
const result = ref(null)
const page = ref(1)
const pageSize = ref(PAGE_SIZES[0])
const loading = ref(false)
const error = ref('')

const windowOptions = computed(() => Object.keys(WINDOWS).map(value => ({
  value,
  label: t(`visualGameAdmin.llm.turnTimelines.list.windows.${value}`),
})))

/** 四个「时刻」指标带 @，两个「时长」指标不带，和时间轴页同一条约定。 */
function formatMetric(key, ms) {
  return MOMENT_METRICS.includes(key) ? formatMoment(ms) : formatSeconds(ms)
}

const summaryItems = computed(() => METRIC_KEYS.map(key => {
  const item = result.value.summary[key]
  return {
    key,
    label: t(`visualGameAdmin.llm.turnTimelines.metrics.${key}`),
    value: item.turns === 0 ? '—' : formatMetric(key, item.p50_ms),
    hint: item.turns === 0
      ? t('visualGameAdmin.llm.turnTimelines.list.summaryEmpty')
      : t('visualGameAdmin.llm.turnTimelines.list.summaryHint', { p90: formatMetric(key, item.p90_ms), turns: item.turns }),
  }
}))

const columns = computed(() => [
  { key: 'started_at', label: t('visualGameAdmin.llm.turnTimelines.list.columns.time'), width: '150px', mono: true, wrap: true },
  { key: 'turn', label: t('visualGameAdmin.llm.turnTimelines.list.columns.turn'), width: '320px', wrap: true },
  { key: 'spread', label: t('visualGameAdmin.llm.turnTimelines.list.columns.spread'), width: '260px' },
  ...METRIC_KEYS.map(key => ({ key, label: t(`visualGameAdmin.llm.turnTimelines.metrics.${key}`), width: '120px', align: 'right', mono: true })),
])

/** 同一页里最慢的那个回合撑满条形图，其余按比例，便于一眼看出哪个回合慢、慢在哪一段。 */
const pageMaxMs = computed(() => Math.max(1, ...result.value.items.map(turnEndMs)))

const rows = computed(() => result.value.items.map(item => ({
  id: item.turn_id,
  started_at: formatDateTime(item.started_at),
  item,
  ...Object.fromEntries(METRIC_KEYS.map(key => [key, item.metrics?.[key] == null ? '—' : formatMetric(key, item.metrics[key].ms)])),
})))

const pageSummary = computed(() => t('visualGameAdmin.llm.turnTimelines.list.pageSummary', {
  total: result.value.total,
  page: page.value,
  pages: Math.max(1, result.value.pages),
}))

function turnEndMs(item) {
  return item.metrics?.input_unlocked?.ms ?? item.server_npc_response_ms ?? item.server_player_kp_ms ?? 0
}

/** 条形图三段：到 KP 出结果、到 NPC 出结果、到输入解锁。缺哪个时刻就少哪一段。 */
function spreadSegments(item) {
  const points = [
    { tone: 'kp', at: item.server_player_kp_ms },
    { tone: 'npc', at: item.server_npc_response_ms },
    { tone: 'rest', at: item.metrics?.input_unlocked?.ms ?? null },
  ].filter(point => point.at !== null)
  const segments = []
  let previous = 0
  for (const point of points) {
    if (point.at <= previous) continue
    segments.push({ tone: point.tone, width: `${(point.at - previous) / pageMaxMs.value * 100}%`, title: `${t(`visualGameAdmin.llm.turnTimelines.list.spread.${point.tone}`)} ${formatMoment(point.at)}` })
    previous = point.at
  }
  return segments
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const until = new Date()
    result.value = await listTurnTimelines({
      since: new Date(until.getTime() - WINDOWS[windowKey.value]).toISOString(),
      until: until.toISOString(),
      page: page.value,
      limit: pageSize.value,
    })
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    loading.value = false
  }
}

function search() {
  page.value = 1
  load()
}

function changePage(next) {
  page.value = next
  load()
}

function changePageSize(size) {
  pageSize.value = size
  search()
}

function openTurn(turnId) {
  router.push({ name: 'llm-turn-timeline-detail', params: { turnId } })
}

onMounted(load)
</script>

<template>
  <section class="turn-timelines-page vga-page">
    <PageHeader :title="t('visualGameAdmin.llm.turnTimelines.title')" />

    <Section>
      <Toolbar>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.turnTimelines.list.window') }}</label>
          <Select v-model="windowKey" :options="windowOptions" size="sm" @update:model-value="search" />
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.llm.turnTimelines.list.turnId') }}</label>
          <Input
            v-model="turnIdInput"
            size="sm"
            :placeholder="t('visualGameAdmin.llm.turnTimelines.list.turnIdPlaceholder')"
            @keyup.enter="turnIdInput.trim() && openTurn(turnIdInput.trim())"
          />
        </div>
        <template #actions>
          <Button size="sm" :disabled="!turnIdInput.trim()" @click="openTurn(turnIdInput.trim())">
            <Search :size="14" />
            {{ t('visualGameAdmin.llm.turnTimelines.list.openTurn') }}
          </Button>
          <Button size="sm" variant="secondary" :loading="loading" @click="search">
            {{ t('visualGameAdmin.llm.turnTimelines.list.refresh') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div v-if="loading && result === null" class="vga-state">
      <Spinner size="lg" :label="t('visualGameAdmin.common.loading')" />
    </div>

    <div v-else-if="error" class="vga-state">
      <p class="vga-error" role="alert">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>

    <template v-else-if="result !== null">
      <Section :title="t('visualGameAdmin.llm.turnTimelines.list.summaryTitle')">
        <TurnTimelineMetrics :items="summaryItems" />
      </Section>

      <EmptyState
        v-if="result.items.length === 0"
        :title="t('visualGameAdmin.llm.turnTimelines.list.emptyTitle')"
        :description="t('visualGameAdmin.llm.turnTimelines.list.emptyDescription')"
      />

      <Section v-else :title="t('visualGameAdmin.llm.turnTimelines.list.turnsTitle')">
        <Table :columns="columns" :rows="rows" clickable @row-click="row => openTurn(row.id)">
          <template #turn="{ row }">
            <div class="turn-cell">
              <div class="turn-cell__head">
                <Badge :variant="row.item.mode === 'say' ? 'info' : 'warning'">{{ t(`visualGameAdmin.llm.turnTimelines.modes.${row.item.mode}`) }}</Badge>
                <strong>{{ row.item.scenario_title }}</strong>
                <span>{{ t('visualGameAdmin.llm.turnTimelines.detail.turnNo', { no: row.item.turn_no }) }}</span>
              </div>
              <span class="turn-cell__input">{{ row.item.input_text }}</span>
            </div>
          </template>
          <template #spread="{ row }">
            <div class="spread" :title="row.item.metrics === null ? t('visualGameAdmin.llm.turnTimelines.list.spread.noClient') : ''">
              <i v-for="(segment, index) in spreadSegments(row.item)" :key="index" :class="`spread__seg spread__seg--${segment.tone}`" :style="{ width: segment.width }" :title="segment.title" />
            </div>
          </template>
        </Table>

        <Pagination
          :has-next="page < result.pages"
          :has-prev="page > 1"
          :loading="loading"
          :prev-label="t('visualGameAdmin.llm.turnTimelines.list.previous')"
          :next-label="t('visualGameAdmin.llm.turnTimelines.list.next')"
          :summary="pageSummary"
          :page-size="pageSize"
          :page-size-options="PAGE_SIZES"
          :page-size-label="t('visualGameAdmin.llm.turnTimelines.list.pageSize')"
          @prev="changePage(page - 1)"
          @next="changePage(page + 1)"
          @update:page-size="changePageSize"
        />
      </Section>
    </template>
  </section>
</template>

<style scoped>
.turn-cell {
  display: grid;
  gap: var(--drama-space-xs);
  min-width: 0;
}

.turn-cell__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-xs) var(--drama-space-sm);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.turn-cell__head strong {
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-sm);
}

.turn-cell__input {
  color: var(--drama-color-text);
  overflow-wrap: anywhere;
}

.spread {
  display: flex;
  height: 12px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--drama-color-surface-alt);
}

.spread__seg { display: block; height: 100%; }
.spread__seg--kp { background: var(--drama-color-info); }
.spread__seg--npc { background: var(--drama-color-success); }
.spread__seg--rest { background: var(--drama-color-text-muted); opacity: 0.5; }
</style>
